// Select-backed dropdown. Typeahead only — use Combobox when the list is large enough to filter.
// Keep alignItemWithTrigger={false}: Select otherwise overlaps the trigger to line up the selected item.
import { Select } from '@base-ui/react/select';
import React, { useState, useEffect, useId } from 'react';
import Tooltip from './Tooltip';
import { CaretDownIcon, Icon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Checkbox from './Checkbox';
import { popupPlacement, type PopupPlacement } from './popupPlacement';

export interface DropdownOption {
  value: string | number;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  /** Shown in the list in place of ``label``, which still names the option in the trigger and for typeahead. */
  content?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  count?: string | number;
}

export type DropdownValue = string | number | string[] | number[];

export interface DropdownProps {
  options: readonly DropdownOption[];
  size?: 'sm' | 'md';
  multiple?: boolean;
  enableSelectAll?: boolean;
  placeholder?: string;
  width?: string;
  maxHeight?: string;
  value?: DropdownValue;
  initialValue?: DropdownValue;
  onChange?: (value: string | string[]) => void;
  className?: string;
  prefix?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  iconOnly?: boolean;
  icon?: Icon;
  trigger?: ((isOpen: boolean) => React.ReactNode) | React.ReactNode;
  /** Set when the custom trigger renders a native button. */
  triggerNativeButton?: boolean;
  triggerClassName?: string;
  menuClassName?: string;
  menuWidth?: number | string;
  placement?: PopupPlacement;
  onOpen?: () => void;
  onClose?: () => void;
}

function normalizeValue(value: DropdownValue | undefined, multiple: boolean): string[] | string {
  if (value !== undefined) {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : value.map(String);
  }
  return multiple ? [] : '';
}

const builtInItemClassName =
  'px-2 py-1 flex items-center gap-2 cursor-pointer rounded-[4px] text-content-default outline-none hover:bg-surface-hover data-[highlighted]:bg-surface-hover data-[selected]:bg-surface-hover data-[selected]:font-medium';

const Dropdown: React.FC<DropdownProps> = ({
  options,
  multiple = false,
  enableSelectAll = false,
  size = 'md',
  placeholder = 'Select...',
  width = 'w-40',
  maxHeight = 'max-h-80',
  value,
  initialValue,
  onChange,
  className,
  prefix,
  disabled = false,
  id,
  ariaLabel,
  iconOnly = false,
  icon: TriggerIcon,
  trigger,
  triggerNativeButton = false,
  triggerClassName,
  menuClassName,
  menuWidth,
  placement = 'bottom-start',
  onOpen,
  onClose
}) => {
  const isControlled = value !== undefined;
  const [uncontrolled, setUncontrolled] = useState(() => normalizeValue(initialValue, multiple));

  useEffect(() => {
    if (!isControlled && initialValue !== undefined) {
      setUncontrolled(normalizeValue(initialValue, multiple));
    }
  }, [initialValue, multiple, isControlled]);

  const selected = isControlled ? normalizeValue(value, multiple) : uncontrolled;
  const setSelected = (next: string[] | string) => {
    if (!isControlled) setUncontrolled(next);
    onChange?.(next);
  };

  const generatedId = useId();
  const buttonId = id || `dropdown-button-${generatedId}`;
  const { side, align } = popupPlacement(placement);
  const customTrigger = trigger != null;

  const sizeClass = { sm: 'h-[28px] p-2', md: 'h-[36px] p-2.5' };
  const iconOnlySizeClass = { sm: 'h-[28px] w-[28px]', md: 'h-[36px] w-[36px]' };
  const fontSizeClass = { sm: 'text-xs', md: 'text-sm' };
  const iconSizeClass = { sm: 14, md: 18 };

  const labelForRawValue = (raw: string) => (raw === 'none' ? 'No access' : raw);

  const getSelectedLabels = (current: string[] | string) => {
    if (multiple) {
      return (current as string[])
        .map((item) => {
          const opt = options.find((o) => String(o.value) === String(item));
          return opt?.label ?? labelForRawValue(String(item));
        })
        .join(', ');
    }
    const selectedValue = current as string;
    const label = options.find((opt) => String(opt.value) === selectedValue)?.label;
    if (label) return label;
    return selectedValue ? labelForRawValue(selectedValue) : '';
  };

  const allValues = options.map((o) => String(o.value));
  const allSelected = multiple && allValues.length > 0 && allValues.every((v) => (selected as string[]).includes(v));
  const someSelected = multiple && (selected as string[]).length > 0 && !allSelected;

  const builtInTriggerClassName = twMerge(
    'border rounded-lg text-content-subtle border-2',
    iconOnly
      ? twMerge('flex items-center justify-center', iconOnlySizeClass[size])
      : twMerge('w-full', sizeClass[size]),
    disabled
      ? 'opacity-60 cursor-not-allowed bg-bluegray-50 border-border-default'
      : 'cursor-pointer hover:bg-surface-hover border-border-default bg-white data-[popup-open]:border-border-active data-[popup-open]:bg-surface-hover'
  );

  const builtInTrigger = (
    <Select.Trigger id={buttonId} aria-label={ariaLabel} className={builtInTriggerClassName}>
      {iconOnly && TriggerIcon ? (
        <TriggerIcon size={iconSizeClass[size]} weight="light" aria-hidden="true" />
      ) : (
        <span className="flex items-center justify-between w-full h-full">
          <Select.Value className="truncate min-w-0 data-[placeholder]:text-content-subtlest text-content-default">
            {(current: string[] | string | null) => {
              const labels = getSelectedLabels(current ?? (multiple ? [] : ''));
              if (!labels) return placeholder;
              return (
                <>
                  {prefix ? <span>{prefix}</span> : null} {labels}
                </>
              );
            }}
          </Select.Value>
          <Select.Icon>
            <CaretDownIcon
              size={iconSizeClass[size]}
              weight="light"
              className="flex-shrink-0 ml-2 transition-transform [[data-popup-open]_&]:rotate-180"
              aria-hidden="true"
            />
          </Select.Icon>
        </span>
      )}
    </Select.Trigger>
  );

  const menuStyle = customTrigger
    ? menuWidth != null
      ? { minWidth: menuWidth, width: menuWidth }
      : { minWidth: 'var(--anchor-width)', width: 'var(--anchor-width)' }
    : undefined;

  return (
    <div
      className={twMerge(
        customTrigger ? 'inline-block' : twMerge('relative flex flex-col gap-2', iconOnly ? 'w-auto' : width),
        fontSizeClass[size],
        className
      )}
    >
      <Select.Root
        value={
          multiple
            ? (selected as string[])
            : options.some((option) => String(option.value) === String(selected))
              ? (selected as string)
              : null
        }
        onValueChange={(next) => {
          if (multiple) {
            setSelected((next ?? []) as string[]);
            return;
          }
          setSelected((next ?? '') as string);
        }}
        multiple={multiple}
        disabled={disabled}
        modal={false}
        onOpenChange={(open) => {
          if (open) onOpen?.();
          else onClose?.();
        }}
      >
        {customTrigger ? (
          <Select.Trigger
            nativeButton={triggerNativeButton}
            aria-label={ariaLabel}
            render={(props, state) => {
              const triggerContent = typeof trigger === 'function' ? trigger(state.open) : trigger;

              if (triggerNativeButton && React.isValidElement<React.HTMLAttributes<HTMLElement>>(triggerContent)) {
                return React.cloneElement(triggerContent, {
                  ...props,
                  className: twMerge(triggerClassName, props.className, triggerContent.props.className)
                });
              }

              return (
                <div
                  {...props}
                  className={twMerge('inline-flex cursor-pointer items-center', triggerClassName, props.className)}
                >
                  {triggerContent}
                </div>
              );
            }}
          />
        ) : iconOnly && ariaLabel ? (
          <Tooltip title={ariaLabel} placement="top" arrow delay={150}>
            {builtInTrigger}
          </Tooltip>
        ) : (
          builtInTrigger
        )}
        <Select.Portal>
          <Select.Positioner
            side={side}
            align={align}
            sideOffset={8}
            alignItemWithTrigger={false}
            className="z-critical"
          >
            <Select.Popup
              className={twMerge(
                'bg-white border-[2px] border-border-default rounded-lg shadow-lg overflow-auto outline-none',
                customTrigger
                  ? twMerge('max-h-72 py-2 px-1.5', menuClassName)
                  : twMerge('pr-0.5 min-w-[var(--anchor-width)]', maxHeight)
              )}
              style={menuStyle}
            >
              {multiple && enableSelectAll && options.length > 0 && (
                // The padding matches the gap above the first option, so the divider sits midway between rows.
                <div
                  className="border-b border-border-subtle pb-2"
                  style={{ margin: '0.5rem 0.375rem 0 0.5rem', width: 'calc(100% - 0.875rem)' }}
                >
                  <button
                    type="button"
                    onClick={() => setSelected(allSelected ? [] : allValues)}
                    className={twMerge(
                      'flex w-full items-center gap-3 px-2 py-1 cursor-pointer rounded-[4px] text-content-default outline-none hover:bg-surface-hover font-medium',
                      fontSizeClass[size]
                    )}
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                      className="pointer-events-none mr-2"
                    />
                    <span className="min-w-0 flex-1 text-left">{allSelected ? 'Clear all' : 'Select all'}</span>
                  </button>
                </div>
              )}
              <Select.List
                className={twMerge(
                  !customTrigger && 'overflow-y-auto custom-scrollbar-thin flex flex-col gap-1.5',
                  fontSizeClass[size]
                )}
                style={customTrigger ? undefined : { padding: '0.5rem 0.375rem 0.5rem 0.5rem' }}
              >
                {options.map((option) => {
                  const optionValue = String(option.value);
                  const optionSelected = multiple
                    ? (selected as string[]).includes(optionValue)
                    : selected === optionValue;
                  const optionContent = (
                    <>
                      {multiple && (
                        <Checkbox
                          checked={optionSelected}
                          readOnly
                          disabled={option.disabled}
                          className={
                            customTrigger
                              ? 'pointer-events-none mr-1 w-4 h-4 rounded flex-shrink-0'
                              : 'pointer-events-none shrink-0'
                          }
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                      )}
                      {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                      {customTrigger ? (
                        <span className="flex min-w-0 flex-1 flex-col">
                          <Select.ItemText className="truncate">{option.content ?? option.label}</Select.ItemText>
                          {option.description && (
                            <span className="text-xs text-content-subtle">{option.description}</span>
                          )}
                        </span>
                      ) : option.description ? (
                        <Tooltip title={option.description} placement="right" arrow delay={150}>
                          <Select.ItemText className="min-w-0 flex-1 truncate">{option.label}</Select.ItemText>
                        </Tooltip>
                      ) : (
                        <Select.ItemText className="min-w-0 flex-1 truncate">
                          {option.content ?? option.label}
                        </Select.ItemText>
                      )}
                      {option.count != null && option.count !== '' && (
                        <span className="ml-auto shrink-0 pl-2 text-xs lining-nums text-content-subtlest">
                          {option.disabled ? 0 : option.count}
                        </span>
                      )}
                    </>
                  );

                  return (
                    <Select.Item
                      key={optionValue}
                      value={optionValue}
                      label={option.label}
                      disabled={option.disabled}
                      className={twMerge(
                        customTrigger
                          ? twMerge(
                              'w-full flex items-center gap-2 px-2 py-1 text-left text-sm text-content-default cursor-pointer rounded-[4px] outline-none transition-colors',
                              'hover:bg-surface-hover data-[highlighted]:bg-surface-hover data-[selected]:font-medium data-[selected]:bg-surface-hover',
                              option.disabled && 'opacity-50 cursor-not-allowed'
                            )
                          : builtInItemClassName,
                        option.className
                      )}
                    >
                      {optionContent}
                    </Select.Item>
                  );
                })}
              </Select.List>
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};

export default Dropdown;
